import React, {useEffect, useState} from 'react';
import {Box, IconButton, styled, Typography, useMediaQuery, useTheme} from '@mui/material';
import {NavLink, useNavigate} from 'react-router-dom';
import {useTranslation} from "react-i18next";
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIosNew';
import NotificationsIcon from '@mui/icons-material/Notifications';
import Template from "../../models/Template";
import {useSelector} from "react-redux";
import {selectSelectedWorkspaceId} from "../../utils/userSlice";
import {AxiosResponse} from "axios/index";
import {useApiService} from "../../context/ApiServiceContext";

// Shimmer effect for loading state
const ShimmerBox = styled(Box)({
    animation: 'shimmer 1.5s infinite linear',
    background: 'linear-gradient(90deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.2) 50%, rgba(255,255,255,0.05) 100%)',
    backgroundSize: '200% 100%',
    borderRadius: '20px',
    '@keyframes shimmer': {
        '0%': {
            backgroundPosition: '200% 0',
        },
        '100%': {
            backgroundPosition: '-200% 0',
        },
    },
});

// Custom styled components
const TemplateButton = styled(NavLink, {
    shouldForwardProp: (prop) => prop !== 'isMobile',
})<{ component?: React.ElementType, isMobile?: boolean }>(({theme, isMobile}) => ({
    display: 'inline-table',
    padding: isMobile ? '6px 12px' : '8px 16px',
    borderRadius: '20px',
    border: '1px solid #FFFFFFA9',
    backgroundColor: 'transparent',
    color: '#ffffff',
    cursor: 'pointer',
    margin: isMobile ? '3px' : '4px',
    fontSize: isMobile ? '12px' : '14px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    maxWidth: isMobile ? '120px' : '200px',
    transition: 'all 0.2s',
    textDecoration: 'none',
    '&:hover': {
        backgroundColor: theme.palette.primary.main,
    },
    '&.active': {
        backgroundColor: theme.palette.primary.main,
        borderColor: theme.palette.primary.main,
        fontWeight: 700,
    }
}));

const BackButton = styled(IconButton, {
    shouldForwardProp: (prop) => prop !== 'isMobile',
})<{ isMobile?: boolean }>(({theme, isMobile}) => ({
    color: 'white',
    borderRadius: '20px',
    padding: isMobile ? '4px 12px' : '6px 16px',
    marginRight: '8px',
    fontSize: isMobile ? '12px' : '14px',
    '& .MuiSvgIcon-root': {
        marginRight: '4px',
        fontSize: '16px',
    },
    '&:hover': {
        backgroundColor: '#3A3D66',
    },
}));

const ButtonContainer = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'isMobile' && prop !== 'isRtl',
})<{ isMobile?: boolean, isRtl?: boolean }>(({theme, isMobile, isRtl}) => ({
        display: 'flex',
        overflowX: 'auto',
        flex: 1,
        paddingBottom: isMobile ? '8px' : '0',
        position: 'relative',
        msOverflowStyle: 'none', /* IE and Edge */
        scrollbarWidth: 'none', /* Firefox */
        '&::-webkit-scrollbar': {
            display: 'none', /* Chrome, Safari, Opera */
        },
        '&:after': {
            content: '""',
            background: `linear-gradient(${isRtl ? '270' : '90'}deg, rgba(0,0,0,0) 0, rgba(5,16,29,1) 100%)`,
            position: 'absolute',
            right: 0,
            top: 0,
            bottom: 0,
            width: '150px',
            pointerEvents: 'none'
        }
    })
);

const ButtonWrapper = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'isMobile',
})<{ isMobile?: boolean }>(({isMobile}) => ({
    display: 'flex',
    overflowX: 'auto',
    paddingRight: '100px',
    msOverflowStyle: 'none', /* IE and Edge */
    scrollbarWidth: 'none', /* Firefox */
    '&::-webkit-scrollbar': {
        display: 'none', /* Chrome, Safari, Opera */
    },
    paddingBottom: isMobile ? '8px' : '0',
}));

const NotificationContainer = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'isMobile',
})<{ isMobile?: boolean }>(({isMobile}) => ({
    display: 'flex',
    alignItems: 'center',
    marginLeft: 'auto',
    padding: isMobile ? '0 8px' : '0 16px',
}));

const ArrowButton = styled(Box)(({theme}) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 25,
    height: 25,
    borderRadius: '50%',
    backgroundColor: theme.palette.primary.main,
    color: 'white',
    marginRight: '10px'
}));

interface TopBarProps {
    onTemplateChange?: (templateId: string) => void;
}

const TopBar: React.FC<TopBarProps> = ({onTemplateChange}) => {
    const {t} = useTranslation();
    const navigate = useNavigate();
    const apiService = useApiService();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [templates, setTemplates] = useState<Template[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const selectedWorkspaceId = useSelector(selectSelectedWorkspaceId);

    const isRtl = theme.direction === 'rtl';

    // Fetch templates when the selected workspace changes
    useEffect(() => {
        setLoading(true);
        if (selectedWorkspaceId) {
            apiService.api.get<Template[]>(`/workspace/${selectedWorkspaceId}/templates?perPage=50`)
                .then((res: AxiosResponse<Template[]>) => {
                    setTemplates(res.data);
                    // console.log("data", res.data)
                    setLoading(false);
                })
                .catch(() => {
                    setLoading(false);
                });
        }
    }, [selectedWorkspaceId]);

    const handleBack = () => {
        navigate("/");
    };

    // Handle template button click to notify parent component
    const handleTemplateClick = (templateId: string) => {
        if (onTemplateChange) {
            onTemplateChange(templateId);
        }
    };

    return (
        <Box sx={{
            mb: isMobile ? 1 : 2,
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            alignItems: isMobile ? 'flex-start' : 'center',
            padding: isMobile ? '8px 0' : '16px 0',
            // flexWrap: 'wrap'
        }}>
            <Box sx={{
                display: 'flex',
                width: isMobile ? '100%' : 'unset',
                alignItems: 'center',
                mb: isMobile ? 1 : 0,
                justifyContent: isMobile ? 'space-between' : 'unset'
            }}>
                <BackButton onClick={handleBack} isMobile={isMobile}>
                    <ArrowButton>
                        {isRtl ? (
                            <ArrowForwardIosIcon/>
                        ) : (
                            <ArrowBackIosIcon/>
                        )}
                    </ArrowButton>
                    {t('main.navbar.back')}
                </BackButton>

                {isMobile && (
                    <NotificationContainer>
                        <NotificationsIcon
                            htmlColor='#C3F3FD'
                            sx={{fontSize: isMobile ? '18px' : '24px', mr: 1}}/>
                        <Typography variant="body2" sx={{color: 'white', fontSize: isMobile ? '12px' : '14px'}}>
                            {t('main.navbar.tip')}
                        </Typography>
                    </NotificationContainer>
                )}
            </Box>

            <ButtonContainer isMobile={isMobile} isRtl={isRtl} sx={{
                width: isMobile ? '100%' : 'unset',
            }}>
                {loading ? (
                    // Show shimmer effect while loading
                    Array.from(new Array(5)).map((_, index) => (
                        <ShimmerBox
                            key={index}
                            sx={{
                                width: isMobile ? '80px' : '120px',
                                height: isMobile ? '30px' : '39px',
                                margin: isMobile ? '3px' : '4px',
                                minWidth: isMobile ? '80px' : '120px',
                            }}
                        />
                    ))
                ) : (
                    <ButtonWrapper>
                        {templates.map((template) => (
                            <TemplateButton
                                key={`template-${template.id}`}
                                component={NavLink}
                                to={`/template/${template.id}`}
                                isMobile={isMobile}
                                className={({isActive}: { isActive: boolean }) => isActive ? "active" : ""}
                                onClick={() => handleTemplateClick(template.id)}
                            >
                                {template.name}
                            </TemplateButton>
                        ))}

                    </ButtonWrapper>
                )}
            </ButtonContainer>

            {!isMobile && (
                <NotificationContainer>
                    <NotificationsIcon
                        htmlColor='#C3F3FD'
                        sx={{fontSize: isMobile ? '18px' : '24px', mr: 1}}/>
                    <Typography variant="body2" sx={{color: 'white', fontSize: isMobile ? '12px' : '14px'}}>
                        {t('main.navbar.tip')}
                    </Typography>
                </NotificationContainer>
            )}

        </Box>
    );
};

export default TopBar;